import React from 'react';

import moment from 'moment';
import uniqBy from 'lodash/uniqBy';
import { useQuery } from '@tanstack/react-query';

import Table from '../../../../../Table';
import TextInput from '../../../../../Form/TextInput';
import InvoicePaymentStatus from './InvoicePaymentStatus';
import Alert from '../../../../../Alert';

import sumDollarAmounts from '../../../../../../helpers/sumDollarAmounts';
import { formatCurrency } from '../../../../../../helpers/currencyMask';
import { useInvoiceTableStore } from '../../../../../../store';
import renderLinksList from '../../../../../../helpers/tables/renderLinksList';
import useSummary from './hooks/useSummary';
import LoanService from '../../../../../../services/LoanService';

const isLineItemPresentInDistributionPlan = (lineItemId, distributionPlan) => {
  return distributionPlan?.some((item) => item.line_item_id === lineItemId);
};

const extractBills = (paymentLineItemsPayment) => {
  const bills = [];

  paymentLineItemsPayment?.forEach((item) => {
    item._bill_line_items?.forEach((billLineItem) => {
      if (billLineItem._bill) {
        bills.push(billLineItem._bill);
      }
    });
  });

  return uniqBy(bills, 'pixel_bill_number');
};

const paidPaymentColumns = [
  {
    title: 'Amounts Distributed',
    keys: ['123'],
    type: 'currency',
    headerTooltip: () => 'The amounts distributed from this payment',
    format: (value, row) => {
      if (row.amountsDistributed) {
        return row.amountsDistributed;
      }

      const billLineItemsAmountsSum = row?._payment_line_items_payment?.map(
        (item) => {
          const sum = sumDollarAmounts(item._bill_line_items, 'amount');

          return { amount: sum };
        }
      );

      return sumDollarAmounts(billLineItemsAmountsSum, 'amount');
    },
  },
  {
    title: 'Bills',
    keys: ['321'],
    headerTooltip: () =>
      'The bills where the distributions were made for this payment',
    format: (value, row) => {
      return renderLinksList({
        data: extractBills(row._payment_line_items_payment),

        createLabel: (item) => {
          return item.pixel_bill_number;
        },
        createLink: (item) =>
          `/loans/${item.loan_id}/bills/${item.id}?tab=Bill`,
      });
    },
  },
];

export default function Invoice({
  invoice,
  distributionPlan,
  skipLineItemsIds,
  setSkipLineItemsIds,
  isPaymentApplied,
  summary,
  lastInvoiceTableRef,
  isPaymentPaid,
  invoicesSource,
  readOnly,
  isBorrower,
  loanId,
}) {
  const { data: demands } = useQuery({
    queryKey: ['demands', loanId],
    queryFn: () => LoanService.getLoanDemands(loanId),
    enabled: !!loanId,
  });

  const hasDemand = demands
    ?.filter((demand) => demand?.status === 'active')
    ?.some((demand) =>
      demand?.invoice_id?.some((inv) => invoice?.id === inv?._invoice?.id)
    );

  const updateInvoiceAmountById = useInvoiceTableStore(
    (state) => state.updateInvoiceAmountById
  );

  // Capitalize the first letter of each word in the status
  const status = invoice?.status
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const lineItemsWithOutstanding =
    invoice?._line_items?.map((item) => {
      return {
        ...item,
        outstanding:
          (item.amount * 100 -
            sumDollarAmounts(item._payment_line_items, 'amount') * 100) /
          100,
      };
    }) || [];

  // if (invoice?._line_items) {
  //   lineItemsWithTotals = [
  //     ...lineItemsWithOutstanding,
  //     {
  //       id: 'total',
  //       description: 'Total',
  //       amount: sumDollarAmounts(invoice._line_items, 'amount'),
  //       /* amountApplied: calculateAmountAppliedTotal(
  //         distributionPlan,
  //         invoice,
  //         isPaymentApplied,
  //         payment?.id
  //       ), */
  //       amountToApply: invoice._line_items.reduce((acc, item) => {
  //         const amountToApply = distributionPlan?.find(
  //           (planItem) => +planItem.line_item_id === +item.id
  //         )?.amount;

  //         if (amountToApply) {
  //           return acc + amountToApply * 100;
  //         }
  //         return acc;
  //       }, 0),
  //       amountApplied: sumDollarAmounts(invoice._line_items, '_payment.total'),
  //       outstanding: sumDollarAmounts(lineItemsWithOutstanding, 'outstanding'),
  //       amountsDistributed: invoice._line_items.reduce((acc, item) => {
  //         const sum = sumDollarAmounts(item._bill_line_items, 'amount');
  //         return acc + sum;
  //       }, 0),
  //       style: 'font-semibold',
  //     },
  //   ];
  // }

  let cols = [
    {
      title: 'Description',
      keys: ['description'],
    },
    {
      keys: ['amount'],
      title: 'Total',
      type: 'optionalTextInput',
      inputType: 'currency',
      isVisible: () => true,
      readOnly: true,
      headerTooltip: () => 'The line item total for this invoice',
    },
    {
      title: 'Outstanding',
      keys: ['outstanding'],
      type: 'currency',
      headerTooltip: () => 'The current outstanding balance for the line item',
    },
    {
      title: 'Amounts Applied',
      keys: ['not_used'],
      headerTooltip: () =>
        'The amounts applied towards the line from this payment',
      format: (value, row) => {
        if (row.id === 'total') {
          /* const totals = lineItemsWithTotals.map((lI) => ({
            total: sumDollarAmounts(lI._payment_line_items, 'amount'),
          }));
          const total = sumDollarAmounts(totals, 'total');
          return total ? `$${formatCurrency(total)}` : '-'; */

          return row.amountApplied
            ? `$${formatCurrency(row.amountApplied)}`
            : '-';
        }

        //   const val = sumDollarAmounts(row._payment_line_items, 'amount');
        const val = row?._payment?.total;

        return val ? `$${formatCurrency(val)}` : '-';
      },
    },
    {
      keys: ['id'],
      title: 'Amount to Apply',
      format: (value) => {
        const row = lineItemsWithOutstanding.find((item) => item.id === value);

        if (row?.amountToApply) {
          updateInvoiceAmountById(invoice.id, row.amountApplied);
          return `${row.amountToApply / 100}`;
        }

        const amountToApply = distributionPlan?.find(
          (planItem) => planItem.line_item_id === +value
        )?.amount;

        return amountToApply ? `${amountToApply}` : '-';
      },
      type: readOnly ? 'text' : 'optionalTextInput',
      inputType: 'currency',
      isVisible: () => true,
      readOnly: true,
    },
    {
      title: 'Action',
      type: 'button',
      width: 74,
      isVisible: (row) => {
        if (readOnly) {
          return false;
        }

        return (
          row.id !== 'total' &&
          !isPaymentApplied &&
          (isLineItemPresentInDistributionPlan(row.id, distributionPlan) ||
            skipLineItemsIds.includes(row.id))
        );
      },
      label: (row) => (skipLineItemsIds.includes(row.id) ? 'Include' : 'Omit'),
      onClick: (row) => {
        if (skipLineItemsIds.includes(row.id)) {
          setSkipLineItemsIds(skipLineItemsIds.filter((id) => id !== row.id));
        } else {
          setSkipLineItemsIds([...skipLineItemsIds, row.id]);
        }
      },
    },
  ];

  // If the payment is applied, 'Ammount to Appy' column should be removed
  if (isPaymentApplied) {
    cols = cols.filter((col) => col.title !== 'Amount to Apply');
  }

  // If payment is paid, omit 'Action' and 'Amounts Applied' columns and add 'Amounts Distributed' and 'Bills' columns
  if (isPaymentPaid && !isBorrower) {
    cols = cols.filter(
      (col) =>
        col.title !== 'Action' &&
        (col.title !== 'Outstanding' ||
          lineItemsWithOutstanding?.at(-1)?.outstanding !== 0)
    );

    cols = [...cols, ...paidPaymentColumns];
  }

  useSummary({
    ref: lastInvoiceTableRef,
    isSummary: summary,
    rows: lineItemsWithOutstanding,
    invoicesSource,
    distributionPlan,
    skipLineItemsIds,
  });

  return (
    <div
      className="mb-10 relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mt-10"
      ref={lastInvoiceTableRef}
    >
      {hasDemand && (
        <div className="mb-5">
          <Alert message="This invoice is included in a demand letter. Applying a payment to the invoice will satisfy the demand." />
        </div>
      )}
      {!summary && (
        <div className="grid gap-5 md:grid-cols-3 xl:grid-cols-4 mb-5">
          <TextInput
            label="Invoice"
            value={invoice?.pixel_invoice_number}
            readOnly
          />
          <TextInput label="Status" value={status} readOnly />
          <TextInput
            label="Due"
            value={
              invoice?.date ? moment(invoice.date).format('MMM D, YYYY') : ''
            }
            readOnly
          />
        </div>
      )}
      <Table
        cols={cols}
        rows={lineItemsWithOutstanding}
        title={summary ? 'Summary Application' : 'Line Items'}
        isFetching={false}
        noCount={summary}
        headerRightContent={
          summary ? null : <InvoicePaymentStatus status={invoice?.status} />
        }
        colsWithTotals={[
          'Total',
          'Outstanding',
          'Amounts Applied',
          'Amount to Apply',
          'Amounts Distributed',
        ]}
        totalTitle="Total"
      />
    </div>
  );
}
